import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Shield, 
  Car, 
  CreditCard, 
  Clock, 
  Globe, 
  CheckCircle2, 
  Users, 
  MapPin,
  Plane,
  HeadphonesIcon,
  Lock,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-background via-background to-orange-500/5 py-20 lg:py-28">
          <div className="container mx-auto px-4 relative">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
                <Star className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-500">Trusted by Thousands of Travelers</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                About <span className="text-orange-500">GoTurkeyTransfer</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground mb-8 font-light">
                Premium Airport Transfers in Turkey, Designed for International Travelers
              </p>
              
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                GoTurkeyTransfer is a trusted digital booking platform that connects travelers with licensed, 
                professional VIP airport transfer services across Turkey. We bring together comfort, 
                reliability, transparency, and global standards to ensure your journey begins the 
                moment you land.
              </p>
            </div>
          </div>
        </section>

        {/* Who We Are */}
        <section className="py-16 lg:py-24 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                    Who We Are
                  </h2>
                  <div className="space-y-4 text-gray-300">
                    <p className="text-lg leading-relaxed">
                      GoTurkeyTransfer is a technology-driven airport transfer booking platform built specifically 
                      for international passengers traveling to Turkey. We operate with a network of 
                      vetted, licensed third-party service providers who meet our strict quality standards.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Our platform was designed to remove uncertainty, eliminate language barriers, 
                      and provide complete transparency in pricing. No surprises, no hidden costs — 
                      just seamless, premium transfers from airport to your destination.
                    </p>
                    <p className="text-lg leading-relaxed">
                      GoTurkeyTransfer manages the entire customer experience: from booking and payment processing 
                      to coordination with local service providers and 24/7 customer support. We act as 
                      your trusted partner throughout your transfer journey.
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                    <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Globe className="h-7 w-7 text-orange-500" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">International Focus</h3>
                    <p className="text-sm text-gray-400">Built for global travelers</p>
                  </div>
                  
                  <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                    <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Shield className="h-7 w-7 text-orange-500" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">Licensed Partners</h3>
                    <p className="text-sm text-gray-400">Vetted service providers</p>
                  </div>
                  
                  <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                    <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="h-7 w-7 text-orange-500" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">Transparent Pricing</h3>
                    <p className="text-sm text-gray-400">No hidden fees ever</p>
                  </div>
                  
                  <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                    <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <HeadphonesIcon className="h-7 w-7 text-orange-500" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">24/7 Support</h3>
                    <p className="text-sm text-gray-400">Always here to help</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Our Mission
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground">
                <p className="leading-relaxed">
                  Our mission is simple: <span className="text-foreground font-medium">to make airport transfers 
                  in Turkey simple, safe, and premium.</span> We believe that your travel experience should 
                  start with confidence, not anxiety.
                </p>
                <p className="leading-relaxed">
                  We provide fixed pricing that you see before you pay, professional drivers who 
                  understand international service standards, and high-comfort vehicles that make 
                  every journey pleasant.
                </p>
                <p className="leading-relaxed">
                  Every traveler should feel confident before they even land. That's what GoTurkeyTransfer delivers.
                </p>
              </div>
              
              <div className="mt-12 flex flex-wrap justify-center gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-500 mb-2">50K+</div>
                  <div className="text-sm text-muted-foreground">Successful Transfers</div>
                </div>
                <div className="w-px bg-border hidden sm:block"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-500 mb-2">98%</div>
                  <div className="text-sm text-muted-foreground">Customer Satisfaction</div>
                </div>
                <div className="w-px bg-border hidden sm:block"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-500 mb-2">24/7</div>
                  <div className="text-sm text-muted-foreground">Customer Support</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How GoTurkeyTransfer Works */}
        <section className="py-16 lg:py-24 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  How GoTurkeyTransfer Works
                </h2>
                <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                  A seamless booking experience designed to give you peace of mind
                </p>
              </div>
              
              <div className="grid md:grid-cols-5 gap-8">
                {[
                  {
                    step: "01",
                    icon: Globe,
                    title: "Book Online",
                    description: "Enter your details and book your transfer in just minutes through our secure platform."
                  },
                  {
                    step: "02",
                    icon: CreditCard,
                    title: "Secure Payment",
                    description: "Pay securely with international payment methods including cards and PayPal."
                  },
                  {
                    step: "03",
                    icon: CheckCircle2,
                    title: "Confirmation",
                    description: "Receive instant confirmation with all details handled by our licensed local partners."
                  },
                  {
                    step: "04",
                    icon: Plane,
                    title: "Flight Tracking",
                    description: "Drivers track your flight in real-time and meet you at arrivals — even if delayed."
                  },
                  {
                    step: "05",
                    icon: HeadphonesIcon,
                    title: "24/7 Support",
                    description: "Clear communication and support available around the clock for any assistance."
                  }
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-2xl mb-4 relative">
                        <item.icon className="h-8 w-8 text-orange-500" />
                        <span className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {item.step}
                        </span>
                      </div>
                      <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-400">{item.description}</p>
                    </div>
                    {index < 4 && (
                      <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-gradient-to-r from-orange-500/50 to-transparent"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Vehicles & Service Quality */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 rounded-3xl p-8 border border-orange-500/20">
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        { icon: Car, label: "Premium Vehicles", desc: "Mercedes Vito & similar" },
                        { icon: Users, label: "Private Transfers", desc: "No shared rides ever" },
                        { icon: Shield, label: "Professional Drivers", desc: "Experienced & vetted" },
                        { icon: CheckCircle2, label: "Child Seats", desc: "Available on request" }
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <item.icon className="h-5 w-5 text-orange-500" />
                          </div>
                          <div>
                            <h4 className="font-medium text-foreground text-sm">{item.label}</h4>
                            <p className="text-xs text-muted-foreground">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="order-1 lg:order-2">
                  <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                    Vehicles & Service Quality
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p className="text-lg leading-relaxed">
                      GoTurkeyTransfer partners only with providers who operate premium-class vehicles. 
                      Think Mercedes Vito, Mercedes E-Class, and similar high-comfort options 
                      that meet international standards.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Every transfer is private — you'll never share your ride with strangers. 
                      Our drivers are professional, experienced, and trained to meet the 
                      expectations of international travelers.
                    </p>
                    <p className="text-lg leading-relaxed">
                      All vehicles are clean, air-conditioned, and equipped with ample luggage 
                      space. Child seats are available on request at no extra charge.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Transparency & Trust */}
        <section className="py-16 lg:py-24 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Transparency & Trust
                </h2>
                <p className="text-lg text-gray-300">
                  Your trust is our foundation. Here's how we protect it.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  {
                    icon: CreditCard,
                    title: "No Hidden Fees",
                    description: "The price you see is the price you pay. No surprise charges, no tips required, no currency tricks."
                  },
                  {
                    icon: CheckCircle2,
                    title: "Fixed Pricing",
                    description: "All pricing is shown upfront before payment. You'll know exactly what you're paying for."
                  },
                  {
                    icon: Lock,
                    title: "GDPR Compliant",
                    description: "Your personal data is handled according to GDPR standards and European privacy regulations."
                  },
                  {
                    icon: Shield,
                    title: "Secure Payments",
                    description: "All payments are processed through secure global providers with bank-level encryption."
                  },
                  {
                    icon: Users,
                    title: "Licensed Partners",
                    description: "We only work with licensed service providers who meet our strict quality requirements."
                  },
                  {
                    icon: HeadphonesIcon,
                    title: "Accountable Support",
                    description: "If something goes wrong, we take responsibility and work to make it right immediately."
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-black/50 border border-white/10 rounded-xl p-6 flex gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Coverage in Turkey */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                    Coverage in Turkey
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p className="text-lg leading-relaxed">
                      GoTurkeyTransfer provides transfer services from all major Turkish airports, 
                      including Istanbul, Antalya, Bodrum, Dalaman, Izmir, and more. 
                      Wherever you're flying into Turkey, we've got you covered.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Our transfers take you directly to hotels, resorts, villas, cruise 
                      ports, or any private address. Door-to-door service means no waiting 
                      at taxi stands or navigating public transport with luggage.
                    </p>
                    <p className="text-lg leading-relaxed">
                      We operate 24/7, including late-night arrivals and early-morning 
                      departures. Your driver will be waiting no matter when your flight lands.
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { city: "Istanbul", airports: "IST, SAW" },
                    { city: "Antalya", airports: "AYT" },
                    { city: "Bodrum", airports: "BJV" },
                    { city: "Dalaman", airports: "DLM" },
                    { city: "Izmir", airports: "ADB" },
                    { city: "Ankara", airports: "ESB" }
                  ].map((item, index) => (
                    <div key={index} className="bg-secondary border border-white/10 rounded-xl p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{item.city}</h4>
                        <p className="text-xs text-gray-400">{item.airports}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose GoTurkeyTransfer */}
        <section className="py-16 lg:py-24 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Why International Travelers Choose GoTurkeyTransfer
                </h2>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { text: "Free Cancellation up to 24 hours", highlight: true },
                  { text: "Free Meet & Greet included (€15 value)", highlight: true },
                  { text: "Designed specifically for international travelers", highlight: false },
                  { text: "Clear, upfront pricing and policies", highlight: false },
                  { text: "Reliable airport pickup with flight tracking", highlight: false },
                  { text: "Dedicated 24/7 customer support team", highlight: false },
                ].map((item, index) => (
                  <div key={index} className={`flex items-start gap-3 rounded-xl p-4 ${
                    item.highlight 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-black/50 border border-white/10'
                  }`}>
                    <CheckCircle2 className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      item.highlight ? 'text-green-400' : 'text-orange-500'
                    }`} />
                    <span className={item.highlight ? 'text-green-400 font-medium' : 'text-white'}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Company Registration - NEW SECTION */}
        <section className="py-12 lg:py-16 bg-background border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-secondary border border-border rounded-2xl p-8">
                <h3 className="text-xl font-bold text-foreground mb-6 text-center">Company Information</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-orange-500 mb-2">VIPGO TECH LTD</h4>
                    <p className="text-muted-foreground text-sm mb-4">
                      GoTurkeyTransfer is operated by VIPGO TECH LTD, a company registered in England and Wales.
                    </p>
                    <div className="space-y-2 text-sm">
                      <p className="text-muted-foreground">
                        <span className="text-foreground font-medium">Company Number:</span> 17039074
                      </p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Registered Office Address</h4>
                    <address className="text-muted-foreground text-sm not-italic leading-relaxed">
                      Unit 501 Leroy House<br />
                      434–436 Essex Road<br />
                      London<br />
                      United Kingdom<br />
                      N1 3FY
                    </address>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-orange-500 to-orange-600 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Travel with Confidence from the Moment You Land
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Book your premium airport transfer today and experience the GoTurkeyTransfer difference. 
                Fixed pricing, professional service, and peace of mind included.
              </p>
              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-white text-orange-500 hover:bg-white/90 text-lg px-8 py-6 h-auto font-semibold shadow-lg"
                  data-testid="book-transfer-cta"
                >
                  Book Your VIP Transfer
                </Button>
              </Link>
              <p className="mt-6 text-white/70 text-sm">
                Free cancellation up to 24 hours before pickup
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
